import { useTranslation } from 'react-i18next';
import { languagesMap } from 'constants/languages';
import CountdownTimer from 'components/atoms/countdown-timer';
import { useQueryClient } from '@tanstack/react-query';
import { getAssignToolQueryKey } from 'infrastructure/hooks/rpm/assign-tool/use-get-assign-tool';

import PatientCell from './patient-cell';
import OptionCell from './option-cell';

import type { IAssignTool } from 'infrastructure/interfaces';
import type { Languages } from 'infrastructure/enums';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

export const useColumns = (
  toggleOpen: () => void,
): IBaseTableColumns<IAssignTool> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return [
    {
      label: t('labels.patientInfo'),
      key: 'guid',
      width: '275px',
      render(guid: string, record) {
        return (
          <PatientCell
            name={record.name}
            guid={record.guid}
            toggleOpen={toggleOpen}
          />
        );
      },
      dataCy: 'assign-tool-table-patient-info',
    },
    {
      label: t('overviewDashboard.dateOfBirth'),
      key: 'dob',
      width: '90px',
      align: 'center',
      dataCy: 'assign-tool-table-dob',
    },
    {
      label: t('labels.preferredLanguage'),
      width: '80px',
      key: 'language',
      align: 'center',
      dataCy: 'assign-tool-table-preferred-language',
      render(language: Languages) {
        return languagesMap[language];
      },
    },
    {
      label: t('overviewDashboard.timeRemaining'),
      width: '75px',
      key: 'guid',
      align: 'center',
      dataCy: 'assign-tool-table-time-remaining',
      render(guid: string, record) {
        const endTime = record.assignee?.expireAtTimestamp
          ? new Date(record.assignee.expireAtTimestamp)
          : null;

        if (!endTime) {
          return null;
        }

        return (
          <CountdownTimer
            endTime={endTime}
            isNegative
            onFinish={() => {
              const queryKey = getAssignToolQueryKey();
              queryClient.invalidateQueries(queryKey);
            }}
          />
        );
      },
    },
    {
      label: 'Options',
      width: '135px',
      key: 'guid',
      align: 'center',
      dataCy: 'assign-tool-table-options',
      render(guid: string) {
        return <OptionCell patientGuid={guid} />;
      },
    },
  ];
};
